import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { TeasingComponent } from "../../CustomComponents/TeasingComponent";
import { ModuleManager } from "../../Managers/ModuleManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { Settings } from "../../CustomComponents/Settings";
import { PolygonElement } from "../../CustomComponents/PolygonElement";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    static INTRO_DELAY: number = 10;
    static OUTRO_DELAY: number = 90;
    static OUTRO_HW_DELAY: number = 90;
    static INTRO_DELAY_GAMBLE: number = 50;

    protected _controller: BaseController;

    private _wasFinished: boolean = false;
    private _initWild: Array<boolean> = [false, false, false, false, false];

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _reelMask: PolygonElement;
    protected _winAnimator: WinAnimatorExtended;

    private _teasingBonus: TeasingBonus;
    protected _transition: SwitchElement;

    private _wildContainer: ContainerElement;
    private _reelsOver: ContainerElement;

    private _background: SwitchElement;
    private _foreground: SwitchElement;
    private _winContainer: ContainerElement;
    private _noWinContainer: ContainerElement;

    private _introLong: boolean = false;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._teasingBonus = new TeasingBonus();
        this._teasingBonus.quality = (<string>window["QUALITY"]).toLowerCase();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "TeasingComponent" && this.childs[key] instanceof TeasingComponent) {
                    this._teasingBonus = this.childs[key];
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                    this._teasingBonus.addElementInstance({ machine: this.childs[key] });
                }

                if (key === "PE_ReelMask" && this.childs[key] instanceof PolygonElement) {
                    this._reelMask = this.childs[key];
                    this._teasingBonus.addElementInstance({ reelMask: this.childs[key] });
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                    this._teasingBonus.addElementInstance({ noWinContainer: this.childs[key] });
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                    this._teasingBonus.addElementInstance({ winContainer: this.childs[key] });
                }

                if (key === "TeasingFront" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingFront: this.childs[key] });
                    }
                }

                if (key === "TeasingReels" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingReels: this.childs[key] });
                    }
                }

                if (key === "TeasingParticles" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingParticles: this.childs[key] });
                    }
                }

                if (key === "TeasingWilds" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingWilds: this.childs[key] });
                    }
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs[key];
                }

                if (key === "WildContainer" && this.childs[key] instanceof ContainerElement) {
                    this._wildContainer = this.childs[key];
                }

                if (key === "ExpandingWildMask" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildMask: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel0" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel0: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel1" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel1: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel2" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel2: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel3" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel3: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel4" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel4: this.childs[key] });
                    }
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                    this._teasingBonus.addElementInstance({ background: this.childs[key] });
                }

                if (key === "Foreground" && this.childs[key] instanceof SwitchElement) {
                    this._foreground = this.childs[key];
                    this._teasingBonus.addElementInstance({ foreground: this.childs[key] });
                }

                if (key === "ReelsOver" && this.childs[key] instanceof ContainerElement) {
                    this._reelsOver = this.childs[key];
                    this._teasingBonus.addElementInstance({ reelsOver: this.childs[key] });
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    updateByNode(node: Element): void {
        super.updateByNode(node);
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                            this._winAnimator.addElementInstance({ wildContainer: this._wildContainer });
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    initIntro(): void {
        if (GameManager.baseResponse.currentWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_CycleLoop");
        } else {
            PlayList.stop("SND_CycleLoop");
            PlayList.loop("SND_BaseLoop");
        }

        if (
            GameManager.baseResponse &&
            GameManager.baseResponse.screen &&
            GameManager.baseResponse.screen.reels !== null &&
            GameManager.baseResponse.screen.reels !== undefined
        ) {
            for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
                if (this.hasReelExpandingWild(i)) {
                    this.childs["ExpandingWildReel" + i].goToCase("SHOWN");
                    this._initWild[i] = true;
                }
            }
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);

        this.toggleElements(true);
    }

    private hasReelExpandingWild(reelIndex: number): boolean {
        let expandingWild = false;
        if (GameManager.baseResponse.screen.reels !== null && GameManager.baseResponse.screen.reels !== undefined) {
            if (
                GameManager.baseResponse.screen.reels[reelIndex].transform !== null &&
                GameManager.baseResponse.screen.reels[reelIndex].transform !== undefined
            ) {
                for (let i: number = 0; i < GameManager.baseResponse.screen.reels[reelIndex].transform.length; i++) {
                    if (GameManager.baseResponse.screen.reels[reelIndex].transform[i] > 0) {
                        expandingWild = true;
                        break;
                    }
                }
            }
        }

        return expandingWild;
    }

    collect(): void {
        // PlayList.stop('SND_FSEndLoop');
        PlayList.stop("SND_CycleLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.reset(true);
    }

    spin(): void {
        // PlayList.stop('SND_FSEndLoop');
        PlayList.stop("SND_CycleLoop");
        PlayList.loop("SND_BaseLoop");

        for (let i: number = 0; i < this._initWild.length; i++) {
            if (this._initWild[i]) {
                this.childs["ExpandingWildReel" + i].goToCase("HIDE");
                this._initWild[i] = false;
            }
        }

        this._machine.respin = null;
        this._machine.spinStart(this._winAnimator.reset(false, true));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): boolean {
        return true;
    }

    enable(): void {
        this.visible = true;
        this._winAnimator.show();
        this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
    }

    disable(): void {
        this._winAnimator.hide();
        this._winAnimator.reset(false);
    }

    spinReceived(): boolean {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);

        let isWildExpectation = false;
        if (
            (Math.floor(Math.random() * this._machine.getRandomWildExpectation() + 1) === 1 ||
                this.isExpandingWild()) &&
            !ModuleManager.freespinsEnabled()
        ) {
            isWildExpectation = true;
        }

        if (
            this.precedingReelsWithSymbols(GameManager.baseResponse.screen.reels.length - 2, Settings.SymbolTeasing) ===
            2
        ) {
            isWildExpectation = false;
        }

        if (isWildExpectation) {
            this._background.goToCase("WILD_EXPECTATION_DELAYED");
            this._foreground.goToCase("WILD_EXPECTATION_DELAYED");

            let reelPositionY = 0;
            if ((<string>window["QUALITY"]).toLowerCase() === "hd") {
                for (let i = 0; i < this._machine.getReelCount(); i++) {
                    if (this.childs["ExpandingWildReel" + i] != null) {
                        reelPositionY = this.childs["ExpandingWildReel" + i].position.y;
                        Tweens.positionTween(this.childs["ExpandingWildReel" + i], {
                            tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                            tweenY: [
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                            ],
                            tweenDuration: 220,
                            tweenDelay: 60,
                            keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                        });
                    }
                }

                reelPositionY = this._machine.position.y;
                Tweens.positionTween(this._machine, {
                    tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelMask.position.y;
                Tweens.positionTween(this._reelMask, {
                    tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._noWinContainer.position.y;
                Tweens.positionTween(this._noWinContainer, {
                    tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._winContainer.position.y;
                Tweens.positionTween(this._winContainer, {
                    tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelsOver.position.y;
                Tweens.positionTween(this._reelsOver, {
                    tweenX: [0, -7, -5, -7, -5, -7, -5, -7, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });
            } else if ((<string>window["QUALITY"]).toLowerCase() === "qhd") {
                for (let i = 0; i < this._machine.getReelCount(); i++) {
                    if (this.childs["ExpandingWildReel" + i] != null) {
                        reelPositionY = this.childs["ExpandingWildReel" + i].position.y;
                        Tweens.positionTween(this.childs["ExpandingWildReel" + i], {
                            tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                            tweenY: [
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                            ],
                            tweenDuration: 220,
                            tweenDelay: 60,
                            keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                        });
                    }
                }

                reelPositionY = this._machine.position.y;
                Tweens.positionTween(this._machine, {
                    tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelMask.position.y;
                Tweens.positionTween(this._reelMask, {
                    tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._noWinContainer.position.y;
                Tweens.positionTween(this._noWinContainer, {
                    tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._winContainer.position.y;
                Tweens.positionTween(this._winContainer, {
                    tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelsOver.position.y;
                Tweens.positionTween(this._reelsOver, {
                    tweenX: [0, -5, -4, -5, -4, -5, -4, -5, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });
            } else {
                for (let i = 0; i < this._machine.getReelCount(); i++) {
                    if (this.childs["ExpandingWildReel" + i] != null) {
                        reelPositionY = this.childs["ExpandingWildReel" + i].position.y;
                        Tweens.positionTween(this.childs["ExpandingWildReel" + i], {
                            tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                            tweenY: [
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                                reelPositionY,
                            ],
                            tweenDuration: 220,
                            tweenDelay: 60,
                            keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                        });
                    }
                }

                reelPositionY = this._machine.position.y;
                Tweens.positionTween(this._machine, {
                    tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelMask.position.y;
                Tweens.positionTween(this._reelMask, {
                    tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._noWinContainer.position.y;
                Tweens.positionTween(this._noWinContainer, {
                    tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._winContainer.position.y;
                Tweens.positionTween(this._winContainer, {
                    tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });

                reelPositionY = this._reelsOver.position.y;
                Tweens.positionTween(this._reelsOver, {
                    tweenX: [0, -10, -8, -10, -8, -10, -8, -10, 0],
                    tweenY: [
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                        reelPositionY,
                    ],
                    tweenDuration: 220,
                    tweenDelay: 60,
                    keyTimes: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1],
                });
            }
        }

        this._machine.setWildExpectation(isWildExpectation);
        return isWildExpectation;
    }

    private isExpandingWild(): boolean {
        let isBonusGame = false;
        if (GameManager.baseResponse.screen.reels !== null && GameManager.baseResponse.screen.reels !== undefined) {
            for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
                if (
                    GameManager.baseResponse.screen.reels[i].transform !== null &&
                    GameManager.baseResponse.screen.reels[i].transform !== undefined
                ) {
                    for (let j: number = 0; j < GameManager.baseResponse.screen.reels[i].transform.length; j++) {
                        if (GameManager.baseResponse.screen.reels[i].transform[j] > 0) {
                            isBonusGame = true;
                        }
                    }
                }
            }
        }

        return isBonusGame;
    }

    public precedingReelsWithSymbols(reelIndex: number, symbolId: number) {
        let count: number = 0;
        for (let i = 0; i <= reelIndex; i++) {
            if (GameManager.baseResponse.screen.reels[i].symbols.indexOf(symbolId) !== -1) {
                count++;
            }
        }
        return count;
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._machine.forceStop,
        );
    }

    machineStopped(): void {
        this._wasFinished = false;

        let isHighWin = this._winAnimator.isHighWin(GameManager.baseResponse.currentWin);

        this._controller.machineStopped(isHighWin);

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        PlayList.stop("SND_BaseWildLoop", 20);
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            //PlayList.loop('SND_CycleLoop');
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        this.toggleElements(true, false);
        // PlayList.stop('SND_CycleLoop');
        // PlayList.loop('SND_BaseLoop');

        this._introLong = long;

        if (this._introLong) {
            if (this._transition != null) {
                this._transition.goToCase("BASE_SHOW");
            }
        }

        this._introDelay.start(BaseView.INTRO_DELAY);
    }

    introFinished(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            if (!PlayList.isPlaying("SND_BaseRecaploop")) {
                PlayList.loop("SND_CycleLoop");
            }
        } else {
            PlayList.stop("SND_CycleLoop");
            PlayList.loop("SND_BaseLoop");
        }

        if (this._introLong) {
            if (this._transition != null) {
                this._transition.goToCase("BASE_INTRO");
            }
        }
        this._controller.introFinished();
    }

    outro(): void {
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_CycleLoop");

        let inHighWins = this._winAnimator.reset(true);

        if (ModuleManager.freespinsActive()) {
            AmbientBackground.changeAmbientBackground("color-1", 300, 60);
            // PlayList.play('SND_FSIntro');

            if (this._transition != null) {
                this._transition.goToCase("BASE_OUTRO");
                PlayList.play("SND_FStransition");
            }

            this._outroDelay.start(BaseView.OUTRO_DELAY);
        } else {
            if (this._wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(60);
                } else {
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroFinished(): void {
        this.toggleElements(true, false);

        this._controller.outroFinished();
    }

    endReceived(): void {}

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            Tweens.removeTweens(this, "all");
            Tweens.alphaTween(this, {
                alphas: visible ? [0, 1] : [1, 0],
                tweenDuration: 15,
            });
        } else {
            this.alpha = visible ? 1 : 0;
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_CycleLoop");
            PlayList.stop("SND_FSLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/